// Utils
import { DefaultCommissionsTier, DefaultCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Constants
const NEW_VEHICLES_LABEL = "New Vehicles";
const PRE_OWNED_VEHICLES_LABEL = "Pre-Owned Vehicles";
const FINANCE_LABEL = "F&I";
const FINANCE_MULTIPLE_INSTANCES = ["New F&I", "Used F&I"];

const getCrossStoreLabel = (type) => {
	const crossStoreLabels = new Map([
		["newVehicles", NEW_VEHICLES_LABEL],
		["preOwned", PRE_OWNED_VEHICLES_LABEL],
		["finance", FINANCE_LABEL],
	]);
	return crossStoreLabels.get(type) || "";
};

const getCommissionContentTitle = (identifier = "", specified = "") => {
	if (identifier !== "finance") {
		return "Total Variable Gross";
	}
	if (!specified) {
		return "Total F&I Gross";
	}
	return `Total ${specified} F&I Gross`;
};

const getSpecificIdentifier = (make = "") => {
	if (make === "F&I") {
		return "";
	}
	if (make.includes("New")) {
		return "New";
	}
	return "Used";
};

const getValidOptions = (options = [], filter = "") => {
	if (!filter) {
		return options;
	}
	return options.filter((option) => option.make.includes(filter));
};

const isAllOutboundSelected = (grossStructure = []) => {
	const outboundOptions = grossStructure.filter((option) => option.make.includes("Outbound"));
	return outboundOptions.every((option) => option.checked);
};

const isSomeOtherOptionSelected = (grossStructure = []) => {
	const options = grossStructure.filter((option) => !option.make.includes("Outbound"));
	return options.some((option) => option.checked);
};

// Check for single or multiple commissions
const isMultipleCommissions = (commissions = null) => Array.isArray(commissions);

const updateCommissionContent = (content, updatedTiers = [], index = -1) => {
	if (index === -1) {
		return { ...content, tiers: updatedTiers };
	}

	if (Array.isArray(content)) {
		content[index]["tiers"] = updatedTiers;
		return content;
	}

	return null;
};

const isDefaultVariableGross = (options = [], filter = "") => {
	const optionsToValidate = getValidOptions(options, filter);
	const isAllOutboundOptionsChecked = isAllOutboundSelected(optionsToValidate);
	const isOtherOptionChecked = isSomeOtherOptionSelected(optionsToValidate);
	return isAllOutboundOptionsChecked && !isOtherOptionChecked;
};

const isOptionsAndTierValid = (options = [], commission = null, filter = "") => {
	const { tiers = [] } = commission || {};
	const optionsToValidate = getValidOptions(options, filter);

	const isAllOutboundOptionsChecked = isAllOutboundSelected(optionsToValidate);
	const isOtherOptionChecked = isSomeOtherOptionSelected(optionsToValidate);
	const tiersRows = tiers?.length || 1;
	const tiersColumns = tiers?.[0]?.commissions?.length || 1;

	const { commission: lastCommission = { value: 0, input: "Percent" } } = tiers?.[tiersRows - 1]?.commissions?.[tiersColumns - 1] || {};

	if (lastCommission?.value) {
		return true;
	}

	return !isOtherOptionChecked && isAllOutboundOptionsChecked;
};

const crossCommissions = (make = "", tiers = [], optionalArgs = {}) => {
	const { applyRetroactively = false, individual = false } = optionalArgs ?? {};
	return {
		tiers,
		make,
		individual,
		applyTiersRetroactively: applyRetroactively,
	};
};

const getDefaultTier = () => {
	const max = 99999.99;
	const maxTier = 9999;
	return DefaultCommissionsTier(-max, max, [DefaultCommissionTier(0, maxTier, { commission: 0, input: "Percent" })]);
};

const getFinanceCommissionsContent = (value) => {
	if (value) {
		return [crossCommissions(FINANCE_LABEL, [getDefaultTier()])];
	}

	return FINANCE_MULTIPLE_INSTANCES.map((make) => crossCommissions(make, [getDefaultTier()]));
};

export {
	getCommissionContentTitle,
	getCrossStoreLabel,
	getDefaultTier,
	getFinanceCommissionsContent,
	getSpecificIdentifier,
	getValidOptions,
	isDefaultVariableGross,
	isMultipleCommissions,
	isAllOutboundSelected,
	isSomeOtherOptionSelected,
	isOptionsAndTierValid,
	updateCommissionContent,
};
